/**
 * Valnexo CRM - Module Detail Page
 * =================================
 * Page de consultation d'un module avec:
 * - Liste des contenus (vidéos, docs, articles)
 * - Lecteur de contenu
 * - Quiz en fin de module
 * - Suivi de progression
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { academyAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
    ArrowLeft, Play, FileText, Video, BookOpen, CheckCircle2,
    Clock, Award, ChevronRight, ChevronLeft, Send, RefreshCw,
    Zap, HardHat, Monitor, Eye, Check, X, AlertCircle, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import ModuleResources from '@/components/academy/ModuleResources';

// Icônes par type de contenu
const CONTENT_ICONS = {
    video: Video,
    document: FileText,
    article: BookOpen
};

// Composant lecteur de contenu — avec suivi vidéo automatique (YT / Vimeo / natif).
// L'auto-marquage "vue" est déclenché par le backend dès 85% ou temps restant ≤ 60s.
const AUTO_VIEWED_THRESHOLD = 85;

/** Charge un script CDN une seule fois par domaine. Retourne une promesse. */
const loadedScripts = {};
function loadScript(src) {
    if (loadedScripts[src]) return loadedScripts[src];
    loadedScripts[src] = new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${src}"]`);
        if (existing) { existing.addEventListener('load', resolve); return; }
        const s = document.createElement('script');
        s.src = src; s.async = true;
        s.onload = resolve;
        s.onerror = () => reject(new Error(`Failed to load ${src}`));
        document.head.appendChild(s);
    });
    return loadedScripts[src];
}

const detectVideoPlatform = (url = '') => {
    if (!url) return 'unknown';
    if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
    if (url.includes('vimeo.com')) return 'vimeo';
    return 'native';
};

const extractYouTubeId = (url) => {
    try {
        if (url.includes('youtu.be/')) return url.split('youtu.be/')[1]?.split(/[?&#]/)[0];
        if (url.includes('youtube.com/watch')) return new URL(url).searchParams.get('v');
        if (url.includes('youtube.com/embed/')) return url.split('embed/')[1]?.split(/[?&#]/)[0];
    } catch (_e) { /* noop */ }
    return null;
};

const extractVimeoId = (url) => {
    const m = url.match(/vimeo\.com\/(\d+)/);
    return m ? m[1] : null;
};

const ContentViewer = ({ content, onComplete }) => {
    const timeSpentRef = useRef(0);
    const timerRef = useRef(null);
    const videoRef = useRef(null);
    const iframeRef = useRef(null);
    // Suivi vidéo : dernières valeurs connues (envoyées au backend)
    const videoStateRef = useRef({ currentTime: 0, duration: 0, maxWatched: 0, autoDone: false });
    const platform = content?.type === 'video' ? detectVideoPlatform(content?.url || '') : null;

    // Reset state à chaque changement de contenu
    useEffect(() => {
        timeSpentRef.current = 0;
        videoStateRef.current = { currentTime: 0, duration: 0, maxWatched: 0, autoDone: false };

        // Signal "session_start" au backend (compteur de sessions)
        if (content?.id) {
            onComplete(content.id, { time_spent_seconds: 0, completed: false, session_start: true });
        }

        if (timerRef.current) clearInterval(timerRef.current);
        // Chrono d'activité générique (utile pour docs/articles + fallback vidéo)
        timerRef.current = setInterval(() => {
            timeSpentRef.current += 1;
            if (timeSpentRef.current % 30 === 0 && content?.id) {
                // Envoie un heartbeat toutes les 30s avec la dernière position vidéo connue
                const vs = videoStateRef.current;
                onComplete(content.id, {
                    time_spent_seconds: 30,
                    completed: false,
                    current_time_seconds: vs.currentTime || undefined,
                    duration_seconds: vs.duration || undefined,
                    watched_percent: vs.maxWatched || undefined,
                });
            }
        }, 1000);

        return () => { if (timerRef.current) clearInterval(timerRef.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content?.id]);

    /** Trigger central pour envoyer une progression vidéo et éventuellement l'auto-marquer. */
    const pushVideoProgress = (cur, dur) => {
        if (!content?.id || !dur || dur <= 0) return;
        const pct = Math.max(0, Math.min(100, (cur / dur) * 100));
        const vs = videoStateRef.current;
        if (pct > vs.maxWatched) vs.maxWatched = pct;
        vs.currentTime = cur;
        vs.duration = dur;

        const remaining = dur - cur;
        const shouldAutoDone =
            !vs.autoDone && (pct >= AUTO_VIEWED_THRESHOLD || (dur >= 90 && remaining <= 60));

        onComplete(content.id, {
            time_spent_seconds: 0, // les 30s heartbeat gèrent le temps
            completed: false,
            current_time_seconds: cur,
            duration_seconds: dur,
            watched_percent: pct,
        });

        if (shouldAutoDone) {
            vs.autoDone = true;
            // Le backend le marquera automatiquement, mais on force l'affichage local
            // en renvoyant `completed=true` pour un feedback immédiat côté UI.
            onComplete(content.id, {
                time_spent_seconds: 0,
                completed: false, // l'auto-marquage est déjà fait côté backend
                current_time_seconds: cur,
                duration_seconds: dur,
                watched_percent: pct,
            });
        }
    };

    // === YouTube IFrame API ===
    useEffect(() => {
        if (platform !== 'youtube' || !content?.url) return;
        const videoId = extractYouTubeId(content.url);
        if (!videoId) return;

        let player = null;
        let pollInterval = null;
        let cancelled = false;

        loadScript('https://www.youtube.com/iframe_api').then(() => {
            const init = () => {
                if (cancelled) return;
                if (!window.YT || !window.YT.Player) return setTimeout(init, 150);
                if (!iframeRef.current) return;
                player = new window.YT.Player(iframeRef.current, {
                    events: {
                        onReady: (e) => {
                            try {
                                const dur = e.target.getDuration();
                                videoStateRef.current.duration = dur || 0;
                            } catch (_e) { /* noop */ }
                        },
                        onStateChange: () => {
                            try {
                                const dur = player.getDuration();
                                const cur = player.getCurrentTime();
                                if (dur && cur != null) pushVideoProgress(cur, dur);
                            } catch (_e) { /* noop */ }
                        },
                    },
                });
                // Sondage toutes les 3s pour capter la progression
                pollInterval = setInterval(() => {
                    try {
                        if (!player || !player.getCurrentTime) return;
                        const dur = player.getDuration();
                        const cur = player.getCurrentTime();
                        if (dur && cur != null) pushVideoProgress(cur, dur);
                    } catch (_e) { /* noop */ }
                }, 3000);
            };
            init();
        }).catch(() => { /* offline / bloqué */ });

        return () => {
            cancelled = true;
            if (pollInterval) clearInterval(pollInterval);
            try { player && player.destroy && player.destroy(); } catch (_e) { /* noop */ }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content?.id, platform]);

    // === Vimeo Player SDK ===
    useEffect(() => {
        if (platform !== 'vimeo' || !content?.url) return;
        let player = null;
        let cancelled = false;
        loadScript('https://player.vimeo.com/api/player.js').then(() => {
            if (cancelled || !window.Vimeo || !iframeRef.current) return;
            try {
                player = new window.Vimeo.Player(iframeRef.current);
                player.getDuration().then((dur) => { videoStateRef.current.duration = dur || 0; }).catch(() => {});
                player.on('timeupdate', (data) => {
                    if (data?.duration && data?.seconds != null) {
                        pushVideoProgress(data.seconds, data.duration);
                    }
                });
                player.on('ended', () => {
                    pushVideoProgress(videoStateRef.current.duration, videoStateRef.current.duration);
                });
            } catch (_e) { /* noop */ }
        }).catch(() => {});
        return () => { cancelled = true; try { player && player.destroy && player.destroy(); } catch (_e) { /* noop */ } };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content?.id, platform]);

    // === Vidéo native — timeupdate ===
    const handleNativeTimeUpdate = () => {
        const el = videoRef.current;
        if (!el || !el.duration || Number.isNaN(el.duration)) return;
        pushVideoProgress(el.currentTime, el.duration);
    };

    const handleMarkComplete = () => {
        onComplete(content.id, { time_spent_seconds: timeSpentRef.current, completed: true });
        toast.success('Contenu marqué comme vu');
    };

    if (!content) {
        return (
            <div className="flex items-center justify-center h-96">
                <p className="text-muted-foreground">Sélectionnez un contenu</p>
            </div>
        );
    }

    // Construire l'URL embed pour YouTube (avec enablejsapi=1 pour le suivi)
    const getYouTubeEmbedUrl = (url) => {
        const videoId = extractYouTubeId(url);
        if (videoId) {
            return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`;
        }
        return url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/');
    };

    const getVimeoEmbedUrl = (url) => {
        const videoId = extractVimeoId(url);
        if (videoId) return `https://player.vimeo.com/video/${videoId}`;
        return url.replace('vimeo.com/', 'player.vimeo.com/video/');
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3">
                <div className="min-w-0 flex-1">
                    <h2 className="text-lg sm:text-xl font-semibold break-words leading-snug">{content.title}</h2>
                    {content.description && (
                        <p className="text-muted-foreground text-xs sm:text-sm mt-1 break-words">{content.description}</p>
                    )}
                </div>
                <Button onClick={handleMarkComplete} variant="outline" size="sm" data-testid="mark-complete-btn" className="w-full sm:w-auto shrink-0">
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Marquer comme vu
                </Button>
            </div>

            <Separator />

            {content.type === 'video' && content.url && (
                <div className="aspect-video bg-black rounded-lg overflow-hidden" data-testid="video-container">
                    {platform === 'youtube' ? (
                        <iframe
                            key={`yt-${content.id}`}
                            ref={iframeRef}
                            id={`yt-player-${content.id}`}
                            className="w-full h-full"
                            src={getYouTubeEmbedUrl(content.url)}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                            allowFullScreen
                            title={content.title}
                            referrerPolicy="strict-origin-when-cross-origin"
                        />
                    ) : platform === 'vimeo' ? (
                        <iframe
                            key={`vm-${content.id}`}
                            ref={iframeRef}
                            className="w-full h-full"
                            src={getVimeoEmbedUrl(content.url)}
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                            title={content.title}
                        />
                    ) : (
                        <video
                            key={`vid-${content.id}`}
                            ref={videoRef}
                            className="w-full h-full"
                            controls
                            src={content.url}
                            playsInline
                            onTimeUpdate={handleNativeTimeUpdate}
                            onEnded={handleNativeTimeUpdate}
                        />
                    )}
                </div>
            )}

            {content.type === 'video' && (
                <div
                    className="rounded-lg border border-indigo-200 bg-indigo-50/70 p-2.5 sm:p-3 flex items-start gap-2"
                    data-testid="video-auto-tracking-hint"
                >
                    <Sparkles className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />
                    <p className="text-[11px] sm:text-xs text-indigo-900 leading-snug">
                        <b>Regarde la vidéo jusqu&apos;au bout.</b> Elle sera automatiquement marquée « vue » à partir de <b>{AUTO_VIEWED_THRESHOLD}%</b> de visionnage — si tu quittes avant, elle reste incomplète et ta formation apparaît « non à jour ».
                    </p>
                </div>
            )}

            {content.type === 'document' && content.url && (
                <div className="space-y-4">
                    <div className="p-6 bg-slate-50 rounded-lg border text-center">
                        <FileText className="h-16 w-16 mx-auto text-slate-400 mb-4" />
                        <p className="font-medium mb-4">{content.title}</p>
                        <Button asChild>
                            <a href={content.url} target="_blank" rel="noopener noreferrer">
                                <Eye className="h-4 w-4 mr-2" />
                                Ouvrir le document
                            </a>
                        </Button>
                    </div>
                </div>
            )}

            {content.type === 'article' && (
                <div className="prose prose-slate max-w-none">
                    <div
                        className="p-6 bg-white rounded-lg border"
                        dangerouslySetInnerHTML={{ __html: content.content_text || '<p>Contenu non disponible</p>' }}
                    />
                </div>
            )}
        </div>
    );
};

// Composant Quiz - Question par question avec correction immédiate
const QuizComponent = ({ quiz, moduleId, onSubmit, previousScore, userQuizState }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [selectedOption, setSelectedOption] = useState(null);
    const [isAnswered, setIsAnswered] = useState(false);
    const [quizFinished, setQuizFinished] = useState(false);
    const [result, setResult] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [navigating, setNavigating] = useState(false);
    const navigate = useNavigate();

    // Verrouillage des anciennes réponses : on n'affiche que les questions NON répondues
    const lockedIds = new Set(userQuizState?.locked_question_ids || []);
    const allQuestions = quiz?.questions || [];
    const pendingQuestions = allQuestions.filter(q => !lockedIds.has(q.id));
    const hasOldAnswers = lockedIds.size > 0;
    const isFullyPassedAndUpToDate = userQuizState?.is_passed && (userQuizState?.pending_count || 0) === 0;

    // Si quiz déjà 100% validé ET aucune nouvelle question en attente, montrer le résultat parfait
    if (isFullyPassedAndUpToDate && previousScore === 100 && !quizFinished && !result) {
        return (
            <div className="space-y-6" data-testid="quiz-results-already-perfect">
                <div className="text-center">
                    <Award className="h-6 w-6 text-yellow-500 mx-auto mb-2" />
                    <h2 className="text-xl font-bold">Résultats du Quiz</h2>
                </div>
                <Card className="border-emerald-400">
                    <CardContent className="py-8 text-center bg-gradient-to-b from-emerald-50 to-white rounded-lg">
                        <CheckCircle2 className="h-16 w-16 mx-auto text-emerald-500 mb-4" />
                        <h3 className="text-2xl font-bold text-emerald-700">Score parfait !</h3>
                        <p className="text-3xl font-bold text-emerald-600 mt-1">100%</p>
                        <p className="text-muted-foreground mt-2">Vous maîtrisez parfaitement ce module.</p>
                    </CardContent>
                </Card>
                <div className="flex flex-col items-center gap-3 pt-2">
                    <Button 
                        onClick={async () => {
                            setNavigating(true);
                            try {
                                const res = await academyAPI.getNextModule(moduleId);
                                if (res.data.has_next) {
                                    navigate(`/academy/module/${res.data.next_module_id}`);
                                } else {
                                    toast.info(res.data.message || "L'Académie est en cours de constitution. Les nouveaux modules arrivent bientôt, un peu de patience !");
                                }
                            } catch {
                                toast.info("L'Académie est en cours de constitution. Les nouveaux modules arrivent bientôt, un peu de patience !");
                            } finally {
                                setNavigating(false);
                            }
                        }}
                        size="lg" 
                        disabled={navigating}
                        data-testid="quiz-continue-btn"
                    >
                        {navigating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
                        Continuer vers le module suivant
                        <ChevronRight className="h-4 w-4 ml-2" />
                    </Button>
                </div>
            </div>
        );
    }

    if (!quiz || !quiz.questions || quiz.questions.length === 0) {
        return (
            <Card>
                <CardContent className="py-8 text-center">
                    <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p>Aucun quiz disponible pour ce module</p>
                </CardContent>
            </Card>
        );
    }

    // Cas particulier : toutes les questions sont verrouillées (l'apporteur a déjà tout répondu)
    // mais le score reste < 100. On affiche son état actuel + bouton de navigation.
    if (pendingQuestions.length === 0 && !quizFinished && !result) {
        const score = userQuizState?.current_score ?? previousScore ?? 0;
        const passed = userQuizState?.is_passed || false;
        return (
            <div className="space-y-6" data-testid="quiz-already-completed">
                <div className="text-center">
                    <Award className="h-6 w-6 text-yellow-500 mx-auto mb-2" />
                    <h2 className="text-xl font-bold">Quiz terminé</h2>
                </div>
                <Card className={passed ? "border-emerald-400" : "border-amber-400"}>
                    <CardContent className={cn(
                        "py-8 text-center rounded-lg",
                        passed ? "bg-gradient-to-b from-emerald-50 to-white" : "bg-gradient-to-b from-amber-50 to-white"
                    )}>
                        {passed ? <CheckCircle2 className="h-16 w-16 mx-auto text-emerald-500 mb-4" /> : <Award className="h-16 w-16 mx-auto text-amber-500 mb-4" />}
                        <h3 className={cn("text-2xl font-bold", passed ? "text-emerald-700" : "text-amber-700")}>
                            {passed ? "Quiz validé !" : "Quiz en attente de nouvelles questions"}
                        </h3>
                        <p className={cn("text-3xl font-bold mt-1", passed ? "text-emerald-600" : "text-amber-600")}>{score}%</p>
                        <p className="text-muted-foreground mt-2">
                            Vous avez répondu à toutes les questions actuellement disponibles.
                        </p>
                    </CardContent>
                </Card>
                <div className="flex justify-center pt-2">
                    <Button onClick={() => navigate('/academy')} variant="outline" data-testid="quiz-back-to-academy">
                        Retour à l&apos;Académie
                    </Button>
                </div>
            </div>
        );
    }

    const questions = pendingQuestions;
    const currentQuestion = questions[currentIndex];
    const totalQuestions = questions.length;
    const isCorrect = isAnswered && selectedOption === currentQuestion.correct_answer;
    const progressPercent = ((currentIndex + (isAnswered ? 1 : 0)) / totalQuestions) * 100;

    const handleSelectAnswer = (optionIndex) => {
        if (isAnswered) return; // Pas de changement après réponse
        setSelectedOption(optionIndex);
    };

    const handleConfirmAnswer = () => {
        if (selectedOption === null || isAnswered) return;
        setIsAnswered(true);
        setAnswers(prev => ({ ...prev, [currentQuestion.id]: selectedOption }));
    };

    const handleNextQuestion = async () => {
        if (currentIndex < totalQuestions - 1) {
            setCurrentIndex(prev => prev + 1);
            setSelectedOption(null);
            setIsAnswered(false);
        } else {
            // Dernière question → soumettre au backend
            setSubmitting(true);
            try {
                const allAnswers = { ...answers, [currentQuestion.id]: selectedOption };
                const res = await academyAPI.submitQuiz(moduleId, allAnswers);
                setResult(res.data);
                setQuizFinished(true);
                onSubmit(res.data);
            } catch (err) {
                toast.error('Erreur lors de la soumission du quiz');
            } finally {
                setSubmitting(false);
            }
        }
    };

    const handleRetry = () => {
        setCurrentIndex(0);
        setAnswers({});
        setSelectedOption(null);
        setIsAnswered(false);
        setQuizFinished(false);
        setResult(null);
    };

    // ===================== ÉCRAN DE RÉSULTATS =====================
    if (quizFinished && result) {
        const isPerfect = result.score === 100;
        const isPassed = result.passed;
        const wrongQuestions = result.results?.filter(r => !r.is_correct) || [];

        return (
            <div className="space-y-6" data-testid="quiz-results">
                {/* Header */}
                <div className="text-center">
                    <Award className="h-6 w-6 text-yellow-500 mx-auto mb-2" />
                    <h2 className="text-xl font-semibold">Résultats du Quiz</h2>
                    <p className="text-muted-foreground text-sm">
                        {result.correct_count} / {result.total_questions} bonnes réponses
                    </p>
                </div>

                {/* Score Card */}
                <Card className={cn(
                    "overflow-hidden",
                    isPerfect && "border-emerald-400",
                    isPassed && !isPerfect && "border-amber-400",
                    !isPassed && "border-rose-400"
                )}>
                    <div className={cn(
                        "py-8 text-center",
                        isPerfect && "bg-gradient-to-b from-emerald-50 to-white",
                        isPassed && !isPerfect && "bg-gradient-to-b from-amber-50 to-white",
                        !isPassed && "bg-gradient-to-b from-rose-50 to-white"
                    )}>
                        {isPerfect ? (
                            <>
                                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                                    <CheckCircle2 className="h-10 w-10 text-emerald-500" />
                                </div>
                                <h3 className="text-2xl font-bold text-emerald-700">
                                    Score parfait !
                                </h3>
                                <p className="text-emerald-600 mt-1 text-lg font-semibold">{result.score}%</p>
                                <p className="text-emerald-600/80 mt-2">
                                    Vous maîtrisez parfaitement ce module.
                                </p>
                            </>
                        ) : isPassed ? (
                            <>
                                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                                    <AlertCircle className="h-10 w-10 text-amber-500" />
                                </div>
                                <h3 className="text-2xl font-bold text-amber-700">
                                    Quiz réussi avec {result.score}%
                                </h3>
                                <p className="text-amber-600 mt-2">
                                    Attention, vous avez eu faux aux questions suivantes :
                                </p>
                            </>
                        ) : (
                            <>
                                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-rose-100 flex items-center justify-center">
                                    <X className="h-10 w-10 text-rose-500" />
                                </div>
                                <h3 className="text-2xl font-bold text-rose-700">
                                    Ce n&apos;est pas encore acquis
                                </h3>
                                <p className="text-rose-600 mt-1 text-lg font-semibold">{result.score}% (minimum requis : {result.passing_score}%)</p>
                                <p className="text-rose-600/80 mt-2" data-testid="quiz-fail-message">
                                    Un minimum de 70 % de bonnes réponses est requis pour valider ce bloc (compréhension minimale). Vous pouvez reprendre le quiz autant de fois que nécessaire — repassez-le pour valider.
                                </p>
                            </>
                        )}
                    </div>
                </Card>

                {/* Détail des erreurs */}
                {wrongQuestions.length > 0 && (
                    <div className="space-y-3">
                        <h4 className="font-semibold text-slate-800 text-sm sm:text-base">
                            Vos erreurs et explications :
                        </h4>
                        {wrongQuestions.map((wq, idx) => {
                            const q = questions.find(qq => qq.id === wq.question_id);
                            if (!q) return null;
                            return (
                                <Card key={wq.question_id} className="border-rose-200 bg-rose-50/30 min-w-0">
                                    <CardContent className="py-3 sm:py-4 px-3 sm:px-6 space-y-2">
                                        <p className="font-medium text-slate-900 flex items-start gap-2 text-sm sm:text-base">
                                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-rose-100 text-rose-600 text-xs flex items-center justify-center mt-0.5">
                                                <X className="h-3.5 w-3.5" />
                                            </span>
                                            <span className="min-w-0 flex-1 break-words leading-snug">{q.question}</span>
                                        </p>
                                        <div className="ml-8 space-y-1 text-xs sm:text-sm">
                                            <p className="text-rose-600 break-words">
                                                Votre réponse : <span className="font-medium">{q.options[wq.user_answer] || '—'}</span>
                                            </p>
                                            <p className="text-emerald-600 break-words">
                                                Bonne réponse : <span className="font-medium">{q.options[wq.correct_answer]}</span>
                                            </p>
                                            {wq.explanation && (
                                                <div className="mt-2 p-2.5 bg-blue-50 rounded-lg text-blue-800 border border-blue-100 break-words">
                                                    <strong>Explication :</strong> {wq.explanation}
                                                </div>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}

                {/* Actions */}
                <div className="flex flex-col items-center gap-3 pt-2">
                    {isPassed ? (
                        <>
                            <Button 
                                onClick={async () => {
                                    setNavigating(true);
                                    try {
                                        const res = await academyAPI.getNextModule(moduleId);
                                        if (res.data.has_next) {
                                            navigate(`/academy/module/${res.data.next_module_id}`);
                                        } else {
                                            toast.info(res.data.message || "L'Académie est en cours de constitution. Les nouveaux modules arrivent bientôt, un peu de patience !");
                                        }
                                    } catch {
                                        toast.info("L'Académie est en cours de constitution. Les nouveaux modules arrivent bientôt, un peu de patience !");
                                    } finally {
                                        setNavigating(false);
                                    }
                                }}
                                size="lg" 
                                disabled={navigating}
                                data-testid="quiz-continue-btn"
                            >
                                {navigating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
                                Continuer vers le module suivant
                                <ChevronRight className="h-4 w-4 ml-2" />
                            </Button>
                            {!isPerfect && (
                                <Button onClick={handleRetry} variant="outline" size="lg" data-testid="quiz-retry-btn">
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                    Ou refaire le test pour atteindre 100%
                                </Button>
                            )}
                        </>
                    ) : (
                        <Button onClick={handleRetry} size="lg" className="bg-rose-600 hover:bg-rose-700" data-testid="quiz-must-retry-btn">
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Refaire le test (obligatoire)
                        </Button>
                    )}
                </div>
            </div>
        );
    }

    // ===================== QUESTION EN COURS =====================
    return (
        <div className="space-y-4 sm:space-y-6" data-testid="quiz-in-progress">
            {/* Rappel important : le quiz est OBLIGATOIRE pour valider le module. */}
            {currentIndex === 0 && !isAnswered && !hasOldAnswers && (
                <div
                    className="rounded-xl border-2 border-yellow-400 bg-gradient-to-r from-yellow-50 to-amber-50 p-3 sm:p-4"
                    data-testid="quiz-mandatory-banner"
                >
                    <div className="flex items-start gap-3">
                        <div className="rounded-full bg-yellow-500 p-1.5 shrink-0">
                            <Award className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="font-bold text-yellow-900 text-sm sm:text-base">
                                Le module n&apos;est validé qu&apos;après le quiz
                            </p>
                            <p className="text-yellow-800 text-xs sm:text-sm mt-0.5 leading-snug">
                                Prends 5 minutes pour répondre sérieusement — il te faut au moins <b>{quiz.passing_score}%</b> de bonnes réponses. Tu peux le repasser autant de fois que nécessaire.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Banner : nouvelles questions ajoutées (l'apporteur a déjà répondu à des questions précédentes) */}
            {hasOldAnswers && (
                <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-900 flex items-start gap-3" data-testid="quiz-updates-banner">
                    <Sparkles className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="font-semibold">Nouvelles questions à répondre</p>
                        <p className="text-orange-800 mt-0.5">
                            {lockedIds.size} ancienne{lockedIds.size > 1 ? 's' : ''} réponse{lockedIds.size > 1 ? 's' : ''} verrouillée{lockedIds.size > 1 ? 's' : ''} · {pendingQuestions.length} nouvelle{pendingQuestions.length > 1 ? 's' : ''} question{pendingQuestions.length > 1 ? 's' : ''} à compléter pour mettre à jour votre score.
                        </p>
                    </div>
                </div>
            )}

            {/* Header + Progress */}
            <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                    <div className="min-w-0 flex-1">
                        <h2 className="text-lg sm:text-xl font-semibold flex items-center gap-2">
                            <Award className="h-5 w-5 text-yellow-500 shrink-0" />
                            <span className="truncate">Quiz de validation</span>
                        </h2>
                        <p className="text-muted-foreground text-xs sm:text-sm">
                            Score minimum requis : {quiz.passing_score}%
                        </p>
                    </div>
                    <Badge variant="outline" className="text-xs sm:text-sm px-2.5 py-0.5 sm:px-3 sm:py-1 self-start sm:self-auto shrink-0">
                        Question {currentIndex + 1} / {totalQuestions}
                    </Badge>
                </div>
                <Progress value={progressPercent} className="h-2" />
            </div>

            {/* Question Card */}
            <Card className={cn(
                "transition-all duration-300 min-w-0",
                isAnswered && isCorrect && "border-emerald-400 shadow-emerald-100 shadow-lg",
                isAnswered && !isCorrect && "border-rose-400 shadow-rose-100 shadow-lg"
            )}>
                <CardHeader className="pb-3 px-4 sm:px-6">
                    <CardTitle className="text-sm sm:text-base flex items-start gap-2 sm:gap-3">
                        <span className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/10 text-primary font-semibold flex items-center justify-center text-sm">
                            {currentIndex + 1}
                        </span>
                        <span className="pt-0.5 sm:pt-1 min-w-0 flex-1 break-words leading-snug">{currentQuestion.question}</span>
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2.5 sm:space-y-3 px-4 sm:px-6">
                    {/* Options */}
                    {currentQuestion.options.map((option, oIndex) => {
                        const isSelected = selectedOption === oIndex;
                        const isCorrectOption = isAnswered && oIndex === currentQuestion.correct_answer;
                        const isWrongSelection = isAnswered && isSelected && oIndex !== currentQuestion.correct_answer;

                        return (
                            <button
                                key={oIndex}
                                onClick={() => handleSelectAnswer(oIndex)}
                                disabled={isAnswered}
                                className={cn(
                                    "w-full flex items-start sm:items-center gap-2.5 sm:gap-3 p-3 sm:p-4 rounded-xl border-2 text-left transition-all duration-200 min-w-0",
                                    // Avant réponse
                                    !isAnswered && !isSelected && "border-slate-200 hover:border-primary/50 hover:bg-primary/5 cursor-pointer",
                                    !isAnswered && isSelected && "border-primary bg-primary/5 ring-2 ring-primary/20",
                                    // Après réponse
                                    isCorrectOption && "border-emerald-500 bg-emerald-50",
                                    isWrongSelection && "border-rose-500 bg-rose-50",
                                    isAnswered && !isCorrectOption && !isWrongSelection && "border-slate-100 opacity-50",
                                    isAnswered && "cursor-default"
                                )}
                                data-testid={`quiz-option-${oIndex}`}
                            >
                                <span className={cn(
                                    "flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium border-2 transition-colors mt-0.5 sm:mt-0",
                                    !isAnswered && !isSelected && "border-slate-300 text-slate-500",
                                    !isAnswered && isSelected && "border-primary bg-primary text-white",
                                    isCorrectOption && "border-emerald-500 bg-emerald-500 text-white",
                                    isWrongSelection && "border-rose-500 bg-rose-500 text-white"
                                )}>
                                    {isCorrectOption ? <Check className="h-4 w-4" /> :
                                     isWrongSelection ? <X className="h-4 w-4" /> :
                                     String.fromCharCode(65 + oIndex)}
                                </span>
                                <span className={cn(
                                    "flex-1 min-w-0 text-sm sm:text-base font-medium break-words leading-snug",
                                    isCorrectOption && "text-emerald-700",
                                    isWrongSelection && "text-rose-700"
                                )}>
                                    {option}
                                </span>
                            </button>
                        );
                    })}

                    {/* Correction + Explication */}
                    {isAnswered && (
                        <div className={cn(
                            "mt-4 p-3 sm:p-4 rounded-xl border transition-all duration-300",
                            isCorrect 
                                ? "bg-emerald-50 border-emerald-200" 
                                : "bg-rose-50 border-rose-200"
                        )}>
                            <div className="flex items-start gap-2.5 sm:gap-3">
                                {isCorrect ? (
                                    <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                                ) : (
                                    <X className="h-5 w-5 text-rose-500 flex-shrink-0 mt-0.5" />
                                )}
                                <div className="min-w-0 flex-1">
                                    <p className={cn("font-semibold text-sm sm:text-base", isCorrect ? "text-emerald-700" : "text-rose-700")}>
                                        {isCorrect ? "Bonne réponse !" : "Mauvaise réponse"}
                                    </p>
                                    {!isCorrect && (
                                        <p className="text-xs sm:text-sm text-rose-600 mt-1 break-words">
                                            La bonne réponse est : <strong>{currentQuestion.options[currentQuestion.correct_answer]}</strong>
                                        </p>
                                    )}
                                    {currentQuestion.explanation && (
                                        <p className="text-xs sm:text-sm mt-2 text-slate-700 break-words">
                                            <strong>Explication :</strong> {currentQuestion.explanation}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Bouton d'action */}
            <div className="flex justify-center">
                {!isAnswered ? (
                    <Button
                        onClick={handleConfirmAnswer}
                        disabled={selectedOption === null}
                        size="lg"
                        className="w-full sm:w-auto sm:min-w-[200px]"
                        data-testid="quiz-confirm-btn"
                    >
                        Valider ma réponse
                    </Button>
                ) : (
                    <Button
                        onClick={handleNextQuestion}
                        size="lg"
                        disabled={submitting}
                        className={cn(
                            "w-full sm:w-auto sm:min-w-[200px]",
                            currentIndex === totalQuestions - 1 && "bg-yellow-500 hover:bg-yellow-600"
                        )}
                        data-testid="quiz-next-btn"
                    >
                        {submitting ? (
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : null}
                        {currentIndex < totalQuestions - 1 ? (
                            <>
                                Question suivante
                                <ChevronRight className="h-4 w-4 ml-2" />
                            </>
                        ) : (
                            <>Voir mes résultats</>
                        )}
                    </Button>
                )}
            </div>
        </div>
    );
};

export default function ModuleDetail() {
    const { moduleId } = useParams();
    const navigate = useNavigate();
    const { user, isAdminFull } = useAuth();
    
    const [loading, setLoading] = useState(true);
    const [module, setModule] = useState(null);
    const [activeContent, setActiveContent] = useState(null);
    const [showQuiz, setShowQuiz] = useState(false);

    useEffect(() => {
        loadModule();
    }, [moduleId]);

    const loadModule = async () => {
        setLoading(true);
        try {
            const res = await academyAPI.getModule(moduleId);
            setModule(res.data);
            // Sélectionner le premier contenu UNIQUEMENT au premier chargement
            setActiveContent(prev => {
                if (!prev && res.data.contents?.length > 0) {
                    return res.data.contents[0];
                }
                return prev;
            });
        } catch (err) {
            console.error('Erreur chargement module:', err);
            const status = err.response?.status;
            if (status === 403) {
                toast.error('Ce module n\'est pas encore publié');
            } else {
                toast.error('Module non trouvé');
            }
            navigate('/academy');
        } finally {
            setLoading(false);
        }
    };

    const handleContentProgress = async (contentId, payload) => {
        // Compat : ancien appel (contentId, timeSpent, completed)
        let body;
        if (typeof payload === 'object' && payload !== null) {
            body = { content_id: contentId, ...payload };
        } else {
            // 3ᵉ argument = completed (legacy)
            body = { content_id: contentId, time_spent_seconds: payload || 0, completed: !!arguments[2] };
        }
        try {
            const res = await academyAPI.updateProgress(moduleId, body);
            // Rafraîchir la vue module si le contenu vient d'être marqué vu
            // (manuellement OU automatiquement par le backend).
            const justViewed = body.completed || res?.data?.viewed_source === 'auto_video';
            if (justViewed) {
                const modRes = await academyAPI.getModule(moduleId);
                setModule(modRes.data);
                if (res?.data?.viewed_source === 'auto_video') {
                    toast.success('Vidéo marquée comme vue automatiquement 🎉', { id: `auto-${contentId}` });
                }
            }
        } catch (err) {
            console.error('Erreur mise à jour progression:', err);
        }
    };

    const handleQuizSubmit = async (result) => {
        // Rafraîchir la progression SANS afficher le skeleton de chargement
        // pour ne pas détruire le QuizComponent et perdre l'écran de résultats
        try {
            const res = await academyAPI.getModule(moduleId);
            setModule(res.data);
        } catch (err) {
            console.error('Erreur rafraîchissement module:', err);
        }
    };

    const isContentViewed = (contentId) => {
        return module?.user_progress?.contents_progress?.[contentId]?.viewed;
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-4">
                    <Skeleton className="h-10 w-64" />
                    <div className="grid gap-4 lg:grid-cols-4">
                        <Skeleton className="h-96" />
                        <Skeleton className="h-96 lg:col-span-3" />
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    if (!module) {
        return (
            <DashboardLayout>
                <div className="text-center py-12">
                    <p>Module non trouvé</p>
                    <Button onClick={() => navigate('/academy')} className="mt-4">
                        Retour à l&apos;Académie
                    </Button>
                </div>
            </DashboardLayout>
        );
    }

    const progress = module.user_progress || {};
    const allContentsViewed = module.contents?.every(c => isContentViewed(c.id));

    return (
        <DashboardLayout>
            <div className="space-y-4 max-w-full overflow-x-hidden" data-testid="module-detail-page">
                {/* Header — stack en mobile, aligné en tablette+ */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                        <Button variant="ghost" size="icon" onClick={() => navigate('/academy')} className="shrink-0">
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <div className="min-w-0 flex-1">
                            <h1 className="text-lg sm:text-2xl font-bold leading-tight break-words">{module.title}</h1>
                            {module.description && (
                                <p className="text-muted-foreground text-xs sm:text-sm break-words line-clamp-2 sm:line-clamp-none">{module.description}</p>
                            )}
                        </div>
                    </div>
                    <div className="shrink-0 sm:text-right w-full sm:w-auto">
                        <div className="text-xs sm:text-sm text-muted-foreground mb-1">Progression</div>
                        <div className="flex items-center gap-2">
                            <Progress value={progress.progress_percent || 0} className="flex-1 sm:w-32 h-2" />
                            <span className="font-medium text-sm">{progress.progress_percent || 0}%</span>
                        </div>
                    </div>
                </div>

                {/* === Bannière pédagogique : rappelle CLAIREMENT comment valider le module === */}
                {(() => {
                    const isQuizPassed = module.user_quiz_state?.is_passed;
                    const isModuleCompleted = progress.progress_percent === 100 && (module.has_quiz ? isQuizPassed : true);
                    if (isModuleCompleted) {
                        return (
                            <div className="rounded-xl border-2 border-emerald-300 bg-gradient-to-r from-emerald-50 to-teal-50 p-3 sm:p-4 flex items-start gap-3" data-testid="module-completed-banner">
                                <div className="rounded-full bg-emerald-500 p-1.5 shrink-0">
                                    <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="font-bold text-emerald-800 text-sm sm:text-base">Module validé !</p>
                                    <p className="text-emerald-700 text-xs sm:text-sm mt-0.5">
                                        Bravo, tu as terminé ce module avec succès.
                                    </p>
                                </div>
                            </div>
                        );
                    }
                    if (module.has_quiz) {
                        return (
                            <div className="rounded-xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50 p-3 sm:p-4 flex items-start gap-3" data-testid="module-quiz-required-banner">
                                <div className="rounded-full bg-amber-500 p-1.5 shrink-0">
                                    <Award className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="font-bold text-amber-900 text-sm sm:text-base">
                                        Ce module se termine par un <span className="underline">quiz obligatoire</span>
                                    </p>
                                    <p className="text-amber-800 text-xs sm:text-sm mt-0.5 leading-snug">
                                        Regarder les vidéos ne suffit <b>pas</b> pour valider. Après chaque contenu, tu dois <b>terminer le quiz avec au moins {module.quiz?.passing_score || 70}%</b> pour que le module soit officiellement acquis.
                                    </p>
                                </div>
                            </div>
                        );
                    }
                    return (
                        <div className="rounded-xl border-2 border-indigo-300 bg-gradient-to-r from-indigo-50 to-blue-50 p-3 sm:p-4 flex items-start gap-3" data-testid="module-videos-only-banner">
                            <div className="rounded-full bg-indigo-500 p-1.5 shrink-0">
                                <Video className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="font-bold text-indigo-900 text-sm sm:text-base">
                                    Module sans quiz — <span className="underline">regarde bien toutes les vidéos jusqu&apos;au bout</span>
                                </p>
                                <p className="text-indigo-800 text-xs sm:text-sm mt-0.5 leading-snug">
                                    Pour valider ce module, chaque vidéo doit être vue à <b>au moins 85%</b> (marquage automatique). N&apos;abandonne pas au milieu — sinon le module reste incomplet et remonte comme non fait à ta direction.
                                </p>
                            </div>
                        </div>
                    );
                })()}

                <div className="grid gap-4 lg:grid-cols-4">
                    {/* Sidebar - Liste des contenus */}
                    <Card className="lg:col-span-1">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm">Contenus du module</CardTitle>
                        </CardHeader>
                        <CardContent className="p-2">
                            <ScrollArea className="h-auto max-h-[280px] lg:max-h-[500px] lg:h-[500px]">
                                <div className="space-y-1">
                                    {module.contents?.map((content, index) => {
                                        const ContentIcon = CONTENT_ICONS[content.type] || BookOpen;
                                        const isViewed = isContentViewed(content.id);
                                        const isActive = activeContent?.id === content.id && !showQuiz;
                                        
                                        return (
                                            <button
                                                key={content.id}
                                                onClick={() => {
                                                    setActiveContent(content);
                                                    setShowQuiz(false);
                                                }}
                                                className={cn(
                                                    "w-full flex items-center gap-2 p-2 rounded-lg text-left text-sm transition-colors",
                                                    isActive ? "bg-primary text-primary-foreground" : "hover:bg-slate-100",
                                                    isViewed && !isActive && "text-emerald-600"
                                                )}
                                            >
                                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs">
                                                    {isViewed ? (
                                                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                                                    ) : (
                                                        index + 1
                                                    )}
                                                </span>
                                                <ContentIcon className="h-4 w-4 flex-shrink-0" />
                                                <span className="truncate">{content.title}</span>
                                            </button>
                                        );
                                    })}
                                    
                                    {/* Quiz */}
                                    {module.has_quiz && (
                                        <>
                                            <Separator className="my-2" />
                                            <button
                                                onClick={() => setShowQuiz(true)}
                                                disabled={!allContentsViewed}
                                                className={cn(
                                                    "w-full flex items-center gap-2 p-2 rounded-lg text-left text-sm transition-colors",
                                                    showQuiz ? "bg-yellow-500 text-white" : "hover:bg-yellow-50",
                                                    !allContentsViewed && "opacity-50 cursor-not-allowed",
                                                    module.user_quiz_state?.is_passed && "text-emerald-600"
                                                )}
                                                data-testid="quiz-sidebar-btn"
                                            >
                                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-100 flex items-center justify-center">
                                                    {module.user_quiz_state?.is_passed ? (
                                                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                                                    ) : (
                                                        <Award className="h-4 w-4 text-yellow-600" />
                                                    )}
                                                </span>
                                                <span>Quiz de validation</span>
                                                {module.user_quiz_state?.has_updates && (
                                                    <Badge variant="outline" className="ml-auto text-[10px] py-0 h-5 border-orange-300 text-orange-700 bg-orange-50">
                                                        Mise à jour
                                                    </Badge>
                                                )}
                                                {!module.user_quiz_state?.has_updates && module.user_quiz_state?.current_score !== null && module.user_quiz_state?.current_score !== undefined && (
                                                    <Badge variant="secondary" className="ml-auto">
                                                        {module.user_quiz_state.current_score}%
                                                    </Badge>
                                                )}
                                            </button>
                                            {!allContentsViewed && (
                                                <p className="text-xs text-muted-foreground px-2">
                                                    Terminez tous les contenus pour accéder au quiz
                                                </p>
                                            )}
                                        </>
                                    )}
                                </div>
                            </ScrollArea>
                        </CardContent>
                    </Card>

                    {/* Zone principale - Contenu ou Quiz */}
                    <Card className="lg:col-span-3 min-w-0">
                        <CardContent className="p-4 sm:p-6">
                            {showQuiz ? (
                                <QuizComponent
                                    quiz={module.quiz}
                                    moduleId={moduleId}
                                    onSubmit={handleQuizSubmit}
                                    previousScore={progress.quiz_score}
                                    userQuizState={module.user_quiz_state}
                                />
                            ) : (
                                <ContentViewer
                                    content={activeContent}
                                    onComplete={handleContentProgress}
                                />
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Trames & supports du module — bien visible */}
                <ModuleResources moduleId={moduleId} canManage={isAdminFull()} />
            </div>
        </DashboardLayout>
    );
}
